import React, { useState, useEffect, useRef } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';


import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { API_URLS } from '../../Services/servicioURL';

interface LineaType {
  cve: string;
  nombre: string;
}

interface SublineaType {
  cve: string;
  cve_linea: string;
  nombre: string;
}

interface MarcaType {
  cve: string;
  nombre: string;
}

interface UnidadType {
  cve: string;
  nombre: string;
}

interface ProductoType {
  cve: string;
  cve_linea: string;
  cve_sublinea: string;
  cve_marca: string;
  cve_unidad: string;
  modelo: string;
  serie: string;
  descripcion: string;
  mantenimiento: boolean;
}

const Productos: React.FC = () => {
  // Estados principales
  const [formData, setFormData] = useState<ProductoType>({
    cve: '', cve_linea: '', cve_sublinea: '', cve_marca: '', cve_unidad: '', modelo: '', serie: '', descripcion: '', mantenimiento: false
  });
  const [lineas, setLineas] = useState<LineaType[]>([]);
  const [sublineas, setSublineas] = useState<SublineaType[]>([]);
  const [filteredSublineas, setFilteredSublineas] = useState<SublineaType[]>([]);
  const [marcas, setMarcas] = useState<MarcaType[]>([]);
  const [unidades, setUnidades] = useState<UnidadType[]>([]);
  const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
  const [loading, setLoading] = useState<boolean>(true);
  const [mostrarTabla, setMostrarTabla] = useState<boolean>(false);
  const [productos, setProductos] = useState<ProductoType[]>([]);
  const [filteredProductos, setFilteredProductos] = useState<ProductoType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const toast = useRef<Toast>(null);
  const claveRef = useRef<HTMLInputElement>(null);

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [lineasData, sublineasData, marcasData, unidadesData] = await Promise.all([
          fetch(API_URLS.lineas.obtener).then(res => res.json()),
          fetch(API_URLS.Sublineas.obtener).then(res => res.json()),
          fetch(API_URLS.marcas.obtener).then(res => res.json()),
          fetch(API_URLS.unidades.obtener).then(res => res.json())
        ]);
        setLineas(lineasData);
        setSublineas(sublineasData);
        setMarcas(marcasData);
        setUnidades(unidadesData);
      } catch (error) {
        console.error('Error al cargar datos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtrar sublineas y productos
  useEffect(() => {
    if (formData.cve_linea && sublineas.length > 0) {
      setFilteredSublineas(sublineas.filter(s => s.cve_linea === formData.cve_linea));
    } else {
      setFilteredSublineas([]);
    }
  }, [formData.cve_linea, sublineas]);

  useEffect(() => {
    if (productos.length > 0) {
      const lowercaseSearch = searchTerm.toLowerCase();
      setFilteredProductos(productos.filter(p => 
        p.cve.toLowerCase().includes(lowercaseSearch) || 
        p.descripcion.toLowerCase().includes(lowercaseSearch) ||
        p.modelo.toLowerCase().includes(lowercaseSearch) ||
        p.serie.toLowerCase().includes(lowercaseSearch)
      ));
    } else {
      setFilteredProductos([]);
    }
  }, [searchTerm, productos]);

  // Buscar producto por clave
  const buscarProductoPorClave = async (clave: string): Promise<ProductoType | null> => {
    try {
      const response = await fetch(API_URLS.productos.obtenerPorCve(clave));
      return response.ok ? await response.json() : null;
    } catch (error) {
      console.error('Error al buscar producto:', error);
      return null;
    }
  };

  // Manejadores de eventos
  const handleClaveKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && formData.cve.trim() !== '') {
      e.preventDefault();
      const producto = await buscarProductoPorClave(formData.cve.trim());
      if (producto) {
        setFormData(producto);
        setMode('view');
        toast.current?.show({ severity: 'success', summary: 'Producto encontrado', detail: 'Datos cargados correctamente', life: 2000 });
      } else {
        toast.current?.show({ severity: 'info', summary: 'Producto nuevo', detail: 'Puede continuar registrando', life: 2000 });
      }
    }
  };

  const handleCambios = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
  setFormData(prev => ({ ...prev, mantenimiento: e.checked || false }));
};

  const handleDropdown = (e: DropdownChangeEvent, field: string) => {
    setFormData(prev => ({ ...prev, [field]: e.value.cve }));
  };

  const limpiarFormulario = () => {
    setFormData({ cve: '', cve_linea: '', cve_sublinea: '', cve_marca: '', cve_unidad: '', modelo: '', serie: '', descripcion: '', mantenimiento: false });
  };

  // Acciones CRUD
  const handleNuevo = () => {
    limpiarFormulario();
    setMode('new');
    setTimeout(() => claveRef.current?.focus(), 100);
  };

  const handleEditar = () => {
    if (!formData.cve) {
      toast.current?.show({ severity: 'warn', summary: 'Advertencia', detail: 'Seleccione un producto para editar', life: 2000 });
      return;
    }
    setMode('edit');
  };

  const handleGuardar = async () => {
    try {
      const url = mode === 'new' ? API_URLS.productos.registrar : API_URLS.productos.actualizar(formData.cve);
      const method = mode === 'new' ? 'POST' : 'PATCH';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) throw new Error('Error al guardar el producto');

      setMode('view');
      toast.current?.show({
        severity: 'success',
        summary: '¡Éxito!',
        detail: mode === 'new' ? 'Producto registrado correctamente' : 'Producto actualizado correctamente',
        life: 2000
      });
    } catch (error) {
      console.error('Error:', error);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al guardar el producto', life: 2000 });
    }
  };

  const handleCancelar = () => {
    setMode('view');
    limpiarFormulario();
  };

  const handleEliminar = async () => {
    if (!formData.cve) {
      toast.current?.show({ severity: 'warn', summary: 'Advertencia', detail: 'Seleccione un producto para eliminar', life: 2000 });
      return;
    }

    const confirmacion = window.confirm('⚠️ ¿Está seguro de eliminar este producto?\n\nEsta acción no se puede deshacer.');
    if (!confirmacion) return;

    try {
      const response = await fetch(API_URLS.productos.eliminar(formData.cve), { method: 'DELETE' });
      if (!response.ok) throw new Error('Error al eliminar el producto');

      toast.current?.show({ severity: 'success', summary: '¡Eliminado!', detail: 'Producto eliminado correctamente', life: 2000 });
      handleCancelar();
    } catch (error) {
      console.error('Error:', error);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al eliminar el producto', life: 2000 });
    }
  };

  // Consulta
  const obtenerProductos = async () => {
    try {
      const response = await fetch(API_URLS.productos.obtener);
      if (!response.ok) throw new Error('Error al obtener los productos');
      const data = await response.json();
      setProductos(data);
      setFilteredProductos(data);
      setSearchTerm('');
    } catch (error) {
      console.error('Error:', error);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al cargar los datos', life: 2000 });
    }
  };

  const handleBuscar = () => {
    obtenerProductos();
    setMostrarTabla(true);
  };

  const cerrarTabla = () => setMostrarTabla(false);

  const handleSeleccionarProducto = (producto: ProductoType) => {
    setFormData(producto);
    setMostrarTabla(false);
  };

  const getModeIndicator = () => {
    switch (mode) {
      case 'new': return { text: 'NUEVO PRODUCTO', color: '#10b981', icon: 'pi-plus' };
      case 'edit': return { text: 'EDITANDO PRODUCTO', color: '#f59e0b', icon: 'pi-pencil' };
      default: return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
    }
  };

  const modeInfo = getModeIndicator();

  return (
    <>
      <Navbar />
      <Sidebar />
      <Toast ref={toast} />

      {/* Modal de consulta mejorado */}
      {mostrarTabla && (
        <div className="overlay" onClick={cerrarTabla} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px', maxWidth: '900px', width: '90%',
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                📦 Consultar Productos
              </h3>
              <Button icon="pi pi-times" className="p-button-text p-button-rounded" onClick={cerrarTabla} style={{ color: '#6b7280' }} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ position: 'relative' }}>
                <i className="pi pi-search" style={{
                  position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                  color: '#9ca3af', fontSize: '0.9rem'
                }}></i>
                <InputText
                  style={{ width: '100%', height: '40px', paddingLeft: '40px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.95rem' }}
                  placeholder="Buscar por clave, descripción, modelo o serie..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div style={{ 
              marginBottom: '15px', fontSize: '0.9em', color: '#6b7280', padding: '8px 12px',
              backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb'
            }}>
              📊 Mostrando <strong>{filteredProductos.length}</strong> de <strong>{productos.length}</strong> productos
            </div>

            <div style={{ 
              flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px',
              maxHeight: 'calc(80vh - 200px)'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>🔑 Clave</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>📝 Descripción</th>
                    
                  </tr>
                </thead>
                <tbody>
                  {filteredProductos.length > 0 ? (
                    filteredProductos.map((producto, index) => (
                      <tr 
                        key={index} 
                        onClick={() => handleSeleccionarProducto(producto)}
                        style={{
                          cursor: 'pointer', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      >
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{producto.cve}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{producto.descripcion}</td>
                        </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                        📭 No se encontraron productos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="content-wrapper" style={{
        backgroundImage: 'url(/dist/img/FondoLogin.png)', backgroundSize: 'cover', backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed', minHeight: '100vh', position: 'relative', padding: '20px'
      }}>
        
        {/* Header principal */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '16px', padding: '24px', marginBottom: '20px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h1 style={{ 
              margin: 0, color: '#1f2937', fontSize: '2rem', fontWeight: '700',
              display: 'flex', alignItems: 'center', gap: '12px'
            }}>
              📦 Gestión de Productos
            </h1>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
              backgroundColor: modeInfo.color, color: 'white', borderRadius: '20px',
              fontSize: '0.85rem', fontWeight: '600'
            }}>
              <i className={`pi ${modeInfo.icon}`}></i>
              {modeInfo.text}
            </div>
          </div>
          <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
        </div>

        {/* Botones de acción */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', padding: '20px', marginBottom: '20px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {mode === 'view' ? (
              <>
                <Button icon="pi pi-search" label="Consultar" className="p-button-info p-button-raised" onClick={handleBuscar}
                  style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                <Button icon="pi pi-plus" label="Nuevo" className="p-button-success p-button-raised" onClick={handleNuevo}
                  style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                <Button icon="pi pi-pencil" label="Editar" className="p-button-warning p-button-raised" onClick={handleEditar}
                  disabled={!formData.cve} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !formData.cve ? 0.5 : 1 }} />
                <Button icon="pi pi-trash" label="Eliminar" className="p-button-danger p-button-raised" onClick={handleEliminar}
                  disabled={!formData.cve} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !formData.cve ? 0.5 : 1 }} />
              </>
            ) : (
              <>
                <Button icon="pi pi-save" label="Guardar" className="p-button-success p-button-raised" onClick={handleGuardar}
                  style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                <Button icon="pi pi-times" label="Cancelar" className="p-button-secondary p-button-raised" onClick={handleCancelar}
                  style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
              </>
            )}
          </div>
        </div>

        {/* Formulario principal */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', padding: '24px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
            {/* Primera fila */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  🔑 Clave <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <InputText 
                  ref={claveRef} name="cve" value={formData.cve} onChange={handleCambios} onKeyDown={handleClaveKeyDown}
                  disabled={mode === 'view' || mode === 'edit'} placeholder="Presione Enter para buscar"
                  style={{ width: '100%', height: '40px', fontSize: '0.95rem', padding: '0 12px', 
                    border: `2px solid ${mode === 'view' || mode === 'edit' ? '#e5e7eb' : '#3b82f6'}`, borderRadius: '8px',
                    backgroundColor: mode === 'view' || mode === 'edit' ? '#f9fafb' : '#ffffff' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  📋 Línea <span style={{ color: '#ef4444' }}>*</span>
                </label>
                {loading ? (
                  <div style={{ padding: '12px', color: '#6b7280' }}>🔄 Cargando...</div>
                ) : (
                  <Dropdown 
                    value={lineas.find(l => l.cve === formData.cve_linea)} options={lineas} optionLabel="nombre" 
                    placeholder="Seleccione línea" onChange={(e) => handleDropdown(e, 'cve_linea')} disabled={mode === 'view'}
                    style={{ width: '100%', height: '40px' }}
                  />
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  📂 Sublínea <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Dropdown
                  value={sublineas.find(s => s.cve === formData.cve_sublinea && s.cve_linea === formData.cve_linea)}
                  options={filteredSublineas} optionLabel="nombre" placeholder="Seleccione sublínea"
                  onChange={(e) => handleDropdown(e, 'cve_sublinea')} disabled={mode === 'view' || !formData.cve_linea}
                  style={{ width: '100%', height: '40px' }}
                />
              </div>
            </div>

            {/* Segunda fila */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  🏷️ Marca <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Dropdown 
                  value={marcas.find(m => m.cve === formData.cve_marca)} options={marcas} optionLabel="nombre"
                  placeholder="Seleccione marca" onChange={(e) => handleDropdown(e, 'cve_marca')} disabled={mode === 'view'}
                  style={{ width: '100%', height: '40px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  📏 Unidad <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Dropdown 
                  value={unidades.find(u => u.cve === formData.cve_unidad)} options={unidades} optionLabel="nombre"
                  placeholder="Seleccione unidad" onChange={(e) => handleDropdown(e, 'cve_unidad')} disabled={mode === 'view'}
                  style={{ width: '100%', height: '40px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  🎯 Modelo <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <InputText 
                  name="modelo" value={formData.modelo} onChange={handleCambios} disabled={mode === 'view'}
                  placeholder="Ingrese el modelo"
                  style={{ width: '100%', height: '40px', fontSize: '0.95rem', padding: '0 12px',
                    border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`, borderRadius: '8px',
                    backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff' }}
                />
              </div>
            </div>

            {/* Tercera fila */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  🔢 Serie <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <InputText 
                  name="serie" value={formData.serie} onChange={handleCambios} disabled={mode === 'view'}
                  placeholder="Ingrese la serie"
                  style={{ width: '100%', height: '40px', fontSize: '0.95rem', padding: '0 12px',
                    border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`, borderRadius: '8px',
                    backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  📝 Descripción
                </label>
                <InputTextarea
                  name="descripcion" value={formData.descripcion} onChange={handleCambios} disabled={mode === 'view'}
                  rows={3} placeholder="Ingrese la descripción del producto..."
                  style={{ width: '100%', fontSize: '0.95rem', padding: '12px',
                    border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`, borderRadius: '8px',
                    backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff', resize: 'vertical' }}
                />
              </div>
            </div>

            {/* Cuarta fila - Campo de mantenimiento */}
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px', 
              padding: '20px', 
              border: '2px solid #e9ecef',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Checkbox
                  inputId="mantenimiento"
                  checked={formData.mantenimiento}
                  onChange={handleCheckboxChange}
                  disabled={mode === 'view'}
                  style={{ 
                    transform: 'scale(1.2)',
                    accentColor: '#10b981'
                  }}
                />
                <label 
                  htmlFor="mantenimiento" 
                  style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#374151',
                    cursor: mode === 'view' ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  🔧 ¿Este producto requiere mantenimiento?
                </label>
              </div>
              <div style={{ 
                marginTop: '8px', 
                fontSize: '0.85rem', 
                color: '#6b7280',
                marginLeft: '32px'
              }}>
                {formData.mantenimiento 
                  ? '✅ Este producto está marcado para recibir servicios de mantenimiento'
                  : '❌ Este producto no requiere servicios de mantenimiento'
                }
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Productos;